import React, { useEffect, useState } from "react";
import BreadCrumbs from "../../components/common/BreadCrumbs";
import Button from "../../components/Button";
import AddScrapModal from "../../components/Modal/addscrapmodal/AddScrapModal";
import SellScrapModal from "../../components/Modal/addscrapmodal/SellScrapModal"; // You must create this modal
import { getReq } from "../../apis/api";
import AllSplitTable from "../../components/DataTable/AllSplitTable";
import Loader from "../../components/loader/Loading";
import { useSnackbar } from 'notistack';
import { money, date } from "../../utils/format";

// the sales list is searched by what was sold or by its day (DD-MM-YYYY as shown)
const SALE_FILTERS = [
  { name: "Scrap Sold", selector: "scrap_name" },
  { name: "Date", selector: (row) => date(row.date) },
  { name: "Buyer", selector: (row) => row.buyer_name || "" }, // owner Q7
  { name: "Payment Mode", selector: (row) => row.payment_mode || "" },
];

const Scrap = () => {
  const [openModal, setOpenModal] = useState(false);
  const [openSellModal, setOpenSellModal] = useState(false);
  const [itemsData, setItemsData] = useState([]);
  const [sellData, setSellData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const handleAddScrapClick = () => setOpenModal(!openModal);
  const handleSellScrapClick = () => setOpenSellModal(!openSellModal);

  const fetchScrapData = async () => {
    try {
      setIsLoading(true);
      const response = await getReq("/fetchScrap");
      if (response.status === 200) {
        const sortedData = response.data.sort((a, b) => b.id - a.id);
        setItemsData(sortedData);
      }
    } catch (error) {
      setItemsData([]); // 404 = no open scrap left (e.g. all sold): the list empties instead of keeping sold rows
      if (error?.response?.status !== 404) enqueueSnackbar("Error fetching scrap data", { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSellScrapData = async () => {
    try {
      const response = await getReq("/fetchScrapSell"); // Replace with actual endpoint
      if (response.status === 200) {
        const sortedData = response.data.sort((a, b) => b.id - a.id);
        setSellData(sortedData);
      }
    } catch (error) {
      setSellData([]);
      if (error?.response?.status !== 404) enqueueSnackbar("Error fetching sell scrap data", { variant: 'error' });
    }
  };

  useEffect(() => {
    fetchScrapData();
    fetchSellScrapData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loads both scrap lists once on mount
  }, []);

  // Columns for Add Scrap Table
  const columns = [
    {
      name: "#",
      selector: (row, index) => index + 1,
      sortable: false,
      width: "64px",
      center: true,
    },
    {
      name: "Type",
      selector: (row) => row.type,
      sortable: true,
      minWidth: "7.81vw",
    },
    {
      name: "Name",
      selector: (row) => (row.note ? `${row.category_name || "-"} (${row.note})` : row.category_name), // NEW-68: returned goods
      sortable: true,
      wrap: true, // SGEW 19 Oct (owner Q9): the note says where the scrap came from ("Split offcuts (approval #n)") - it was cut
      minWidth: "7.81vw",
    },
    // {
    //   name: "Weight (kg)",
    //   selector: (row) => row.weight,
    //   sortable: true,
    //   minWidth: "7.81vw",
    // },
    {
      name: "Scrap Weight (kg)",
      selector: (row) => row.scrap_weight,
      sortable: true,
      minWidth: "7.81vw",
    },
    {
      name: "Date",
      selector: (row) => row.date, // raw, so the column sorts by date
      format: (row) => date(row.date), // FIX_RULES 10.3: one date format
      sortable: true,
      minWidth: "7.81vw",
    },
  ];

  // Columns for Sell Scrap Table
  const sellColumns = [
    {
      name: "#",
      selector: (row, index) => index + 1,
      sortable: false,
      width: "64px",
      center: true,
    },
    {
      name: "Scrap Sold", // SGEW 19 Oct (field audit): what each sale was (the list showed weight, date and amount only)
      selector: (row) => row.scrap_name || "-",
      sortable: true,
      wrap: true,
      minWidth: "12vw",
    },
    {
      name: "Scrap Weight (kg)",
      selector: (row) => row.weight,
      sortable: true,
      minWidth: "7.81vw",
    },
    {
      name: "Date",
      selector: (row) => row.date, // raw, so the column sorts by date
      format: (row) => date(row.date), // FIX_RULES 10.3: one date format
      sortable: true,
      minWidth: "7.81vw",
    },
    {
      name: "Amount",
      selector: (row) => row.amount,
      format: (row) => money(row.amount),
      sortable: true,
      minWidth: "7.81vw",
    },
    // SGEW 19 Oct (owner Q7): how each sale was paid and who bought it ("-" = sold before 19 Oct, not recorded)
    {
      name: "Payment Mode",
      selector: (row) => row.payment_mode || "-",
      sortable: true,
      minWidth: "7.81vw",
    },
    {
      name: "Buyer",
      selector: (row) => row.buyer_name || "-",
      sortable: true,
      wrap: true,
      minWidth: "9vw",
    },
  ];

  const onItemAdded = () => {
    fetchScrapData();
    fetchSellScrapData();
  };


  return (
    <div>
      {/* Add Scrap Section */}
      <div className="flex justify-between items-center bg-white rounded-[0.42vw] w-full py-[1vw] md:py-[0.5vw] my-[2vw] md:my-[1vw] px-[4vw] md:px-[2vw] ">
        <BreadCrumbs title={"Home  /  Items / Scraps "} />
        <Button title="Add Scrap" func={handleAddScrapClick} />
      </div>

      <main>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <AllSplitTable
              columns={columns}
              data={itemsData}
              onItemAdded={onItemAdded}
            />

            <div className="text-right font-semibold pr-6 mt-2">
              Total Scrap Weight:{" "}
              {itemsData
                .reduce((total, item) => total + parseFloat(item.scrap_weight || 0), 0)
                .toFixed(2)}{" "}
              kg
            </div>
          </>
        )}
      </main>


      {/* Sell Scrap Section */}
      <div className="flex justify-between items-center bg-white rounded-[0.42vw] w-full py-[1vw] md:py-[0.5vw] my-[2vw] md:my-[1vw] px-[4vw] md:px-[2vw] ">
        <h2 className="font-semibold text-[1.1vw]">Sell Scrap</h2>
        <Button title="Sell Scrap" func={handleSellScrapClick} />
      </div>

      <main>
        <AllSplitTable
          columns={sellColumns}
          data={sellData}
          onItemAdded={onItemAdded}
          filterColumns={SALE_FILTERS}
        />
        <div className="text-right font-semibold pr-6 mt-2">
          Total Sell Weight:{" "}
          {sellData
            .reduce((total, item) => total + parseFloat(item.weight || 0), 0)
            .toFixed(2)}{" "}
          kg
        </div>
      </main>


      {/* Modals */}
      {openModal && (
        <AddScrapModal
          open={openModal}
          handleClose={handleAddScrapClick}
          onItemAdded={onItemAdded}

        />
      )}
      {openSellModal && (
        <SellScrapModal
          open={openSellModal}
          onClose={handleSellScrapClick}
          onItemAdded={onItemAdded}

        />
      )}
    </div>
  );
};

export default Scrap;
